/*
 * @Copyright 2024. Institute for Future Intelligence, Inc.
 */

export enum MolecularViewerColoring {
  Element = 'Element',
  Residue = 'Residue',
  Chain = 'Chain',
  SecondaryStructure = 'Secondary Structure',
  Hydrophobicity = 'Hydrophobicity',
  Occupancy = 'Occupancy',
  Temperature = 'Temperature',
  Sequence = 'Sequence',
  Conformation = 'Conformation',
  Molecule = 'Molecule',
  // Protein Lab only: evolutionary conservation of the shown chain, computed
  // from its UniProt family (biomolecule/conservationService.ts) and painted
  // through the B-factor column — not a chem-lib colorer, so COLORING_MAP
  // carries an Element fallback for the moment before the scores arrive.
  Conservation = 'Conservation',
}

export const COLORING_MAP = new Map(
  [
    { key: MolecularViewerColoring.Element, value: 'EL' },
    { key: MolecularViewerColoring.Residue, value: 'RT' },
    { key: MolecularViewerColoring.Chain, value: 'CH' },
    { key: MolecularViewerColoring.SecondaryStructure, value: 'SS' },
    { key: MolecularViewerColoring.Hydrophobicity, value: 'HY' },
    { key: MolecularViewerColoring.Occupancy, value: 'OC' },
    { key: MolecularViewerColoring.Temperature, value: 'TM' },
    { key: MolecularViewerColoring.Sequence, value: 'SQ' },
    { key: MolecularViewerColoring.Conformation, value: 'CF' },
    { key: MolecularViewerColoring.Molecule, value: 'MO' },
    { key: MolecularViewerColoring.Conservation, value: 'EL' },
  ].map((obj) => [obj.key, obj.value]),
);

export const CHAMBER_COLORING_LABELS = [
  { value: MolecularViewerColoring.Element, label: 'molecularViewer.Element' },
  { value: MolecularViewerColoring.Residue, label: 'molecularViewer.Residue' },
  { value: MolecularViewerColoring.Chain, label: 'molecularViewer.Chain' },
  { value: MolecularViewerColoring.SecondaryStructure, label: 'molecularViewer.SecondaryStructure' },
  { value: MolecularViewerColoring.Hydrophobicity, label: 'molecularViewer.Hydrophobicity' },
  { value: MolecularViewerColoring.Occupancy, label: 'molecularViewer.Occupancy' },
  { value: MolecularViewerColoring.Temperature, label: 'molecularViewer.Temperature' },
  { value: MolecularViewerColoring.Sequence, label: 'molecularViewer.Sequence' },
  { value: MolecularViewerColoring.Conformation, label: 'molecularViewer.Conformation' },
  { value: MolecularViewerColoring.Molecule, label: 'molecularViewer.Molecule' },
];

// The Protein Lab's coloring list: the chem-lib colorings plus Conservation.
export const BIO_COLORING_LABELS = [
  ...CHAMBER_COLORING_LABELS,
  { value: MolecularViewerColoring.Conservation, label: 'molecularViewer.Conservation' },
];

export enum MolecularViewerStyle {
  AtomIndex = 'Atom Index',
  Sketch2D = '2D Sketch',
  Sketch2DCharge = '2D Sketch (Charges)',
  BallAndStick = 'Ball-and-Stick',
  Wireframe = 'Wireframe',
  Stick = 'Stick',
  SpaceFilling = 'Space-Filling',
  Cartoon = 'Cartoon',
  Trace = 'Trace',
  Tube = 'Tube',
  QuickSurface = 'Quick Surface',
  ContactSurface = 'Contact Surface',
  SolventAccessibleSurface = 'Solvent Accessible Surface',
  SolventExcludedSurface = 'Solvent Excluded Surface',
  ElectrostaticPotentialSurface = 'Electrostatic Potential Surface',
  DotSurface = 'Dot Surface',
}

export enum MolecularViewerMaterial {
  Diffuse = 'Diffuse',
  Soft = 'Soft',
  Glossy = 'Glossy',
  Metal = 'Metal',
  Translucent = 'Translucent',
  Glass = 'Glass',
  Backdrop = 'Backdrop',
  Toon = 'Toon',
  Flat = 'Flat',
}

export const MATERIAL_MAP = new Map(
  [
    { key: MolecularViewerMaterial.Diffuse, value: 'DF' },
    { key: MolecularViewerMaterial.Soft, value: 'SF' },
    { key: MolecularViewerMaterial.Glossy, value: 'PL' },
    { key: MolecularViewerMaterial.Metal, value: 'ML' },
    { key: MolecularViewerMaterial.Translucent, value: 'TR' },
    { key: MolecularViewerMaterial.Glass, value: 'GL' },
    { key: MolecularViewerMaterial.Backdrop, value: 'BA' },
    { key: MolecularViewerMaterial.Toon, value: 'TN' },
    { key: MolecularViewerMaterial.Flat, value: 'FL' },
  ].map((obj) => [obj.key, obj.value]),
);

export const MATERIAL_LABELS = [
  { value: MolecularViewerMaterial.Diffuse, label: 'molecularViewer.DiffuseMaterial' },
  { value: MolecularViewerMaterial.Soft, label: 'molecularViewer.SoftMaterial' },
  { value: MolecularViewerMaterial.Glossy, label: 'molecularViewer.GlossyMaterial' },
  { value: MolecularViewerMaterial.Metal, label: 'molecularViewer.MetalMaterial' },
  { value: MolecularViewerMaterial.Translucent, label: 'molecularViewer.TranslucentMaterial' },
  { value: MolecularViewerMaterial.Glass, label: 'molecularViewer.GlassMaterial' },
  { value: MolecularViewerMaterial.Backdrop, label: 'molecularViewer.BackdropMaterial' },
  { value: MolecularViewerMaterial.Toon, label: 'molecularViewer.ToonMaterial' },
  { value: MolecularViewerMaterial.Flat, label: 'molecularViewer.FlatMaterial' },
];

export const STYLE_MAP = new Map(
  [
    { key: MolecularViewerStyle.BallAndStick, value: 'BS' },
    { key: MolecularViewerStyle.AtomIndex, value: 'LN' },
    { key: MolecularViewerStyle.Wireframe, value: 'LN' },
    { key: MolecularViewerStyle.Stick, value: 'LC' },
    { key: MolecularViewerStyle.SpaceFilling, value: 'VW' },
    { key: MolecularViewerStyle.Cartoon, value: 'CA' },
    { key: MolecularViewerStyle.Trace, value: 'TR' },
    { key: MolecularViewerStyle.Tube, value: 'TU' },
    { key: MolecularViewerStyle.QuickSurface, value: 'QS' },
    { key: MolecularViewerStyle.ContactSurface, value: 'CS' },
    { key: MolecularViewerStyle.SolventAccessibleSurface, value: 'SA' },
    { key: MolecularViewerStyle.SolventExcludedSurface, value: 'SE' },
    // ESP surface uses Solvent-Excluded Surface geometry (Connolly-style,
    // 1.4 Å water probe), which is the standard ESP-rendering convention in
    // tools like PyMOL / ChimeraX — better-defined ESP at the surface than a
    // bare VdW envelope. The partial-charge coloring is wired separately in
    // the renderer via the temperature channel (see galleryViewer.tsx).
    { key: MolecularViewerStyle.ElectrostaticPotentialSurface, value: 'SE' },
    // Dot surface piggybacks on Solvent-Accessible Surface geometry — its
    // tessellated mesh provides plenty of vertices to scatter dots across.
    // (Bare VdW envelope would be the textbook "Jmol dots" target, but
    // Miew's VdW mode renders sphere sprites that don't have surface vertices
    // for us to pick up — and disabling sprites globally breaks Ball-and-
    // Stick rendering. Practical compromise.)
    { key: MolecularViewerStyle.DotSurface, value: 'SA' },
  ].map((obj) => [obj.key, obj.value]),
);

export const INDIVIDUAL_MOLECULE_STYLE_LABELS = [
  { value: MolecularViewerStyle.BallAndStick, label: 'molecularViewer.BallAndStick' },
  { value: MolecularViewerStyle.Wireframe, label: 'molecularViewer.Wireframe' },
  { value: MolecularViewerStyle.Stick, label: 'molecularViewer.Stick' },
  { value: MolecularViewerStyle.SpaceFilling, label: 'molecularViewer.SpaceFilling' },
  { value: MolecularViewerStyle.Cartoon, label: 'molecularViewer.Cartoon' },
  { value: MolecularViewerStyle.Trace, label: 'molecularViewer.Trace' },
  { value: MolecularViewerStyle.Tube, label: 'molecularViewer.Tube' },
  { value: MolecularViewerStyle.QuickSurface, label: 'molecularViewer.QuickSurface' },
  { value: MolecularViewerStyle.ContactSurface, label: 'molecularViewer.ContactSurface' },
  { value: MolecularViewerStyle.SolventAccessibleSurface, label: 'molecularViewer.SolventAccessibleSurface' },
  { value: MolecularViewerStyle.SolventExcludedSurface, label: 'molecularViewer.SolventExcludedSurface' },
  {
    value: MolecularViewerStyle.ElectrostaticPotentialSurface,
    label: 'molecularViewer.ElectrostaticPotentialSurface',
  },
  { value: MolecularViewerStyle.DotSurface, label: 'molecularViewer.DotSurface' },
];

export const CHAMBER_STYLE_LABELS = [
  { value: MolecularViewerStyle.BallAndStick, label: 'molecularViewer.BallAndStick' },
  { value: MolecularViewerStyle.Wireframe, label: 'molecularViewer.Wireframe' },
  { value: MolecularViewerStyle.AtomIndex, label: 'molecularViewer.AtomIndex' },
  { value: MolecularViewerStyle.Stick, label: 'molecularViewer.Stick' },
  { value: MolecularViewerStyle.SpaceFilling, label: 'molecularViewer.SpaceFilling' },
  { value: MolecularViewerStyle.Cartoon, label: 'molecularViewer.Cartoon' },
  { value: MolecularViewerStyle.Trace, label: 'molecularViewer.Trace' },
  { value: MolecularViewerStyle.Tube, label: 'molecularViewer.Tube' },
  { value: MolecularViewerStyle.QuickSurface, label: 'molecularViewer.QuickSurface' },
  { value: MolecularViewerStyle.ContactSurface, label: 'molecularViewer.ContactSurface' },
  { value: MolecularViewerStyle.SolventAccessibleSurface, label: 'molecularViewer.SolventAccessibleSurface' },
  { value: MolecularViewerStyle.SolventExcludedSurface, label: 'molecularViewer.SolventExcludedSurface' },
  {
    value: MolecularViewerStyle.ElectrostaticPotentialSurface,
    label: 'molecularViewer.ElectrostaticPotentialSurface',
  },
  { value: MolecularViewerStyle.DotSurface, label: 'molecularViewer.DotSurface' },
];

// Crystal lattices have no protein backbone, so the Cα-trace representations
// (Cartoon / Trace / Tube) render nothing meaningful — drop them from the
// crystal lab's style picker. (The right-click style submenu already hides
// them via the !multipleResidues guard in sharedMenuItems.)
export const CRYSTAL_STYLE_LABELS = CHAMBER_STYLE_LABELS.filter(
  (o) =>
    o.value !== MolecularViewerStyle.Cartoon &&
    o.value !== MolecularViewerStyle.Trace &&
    o.value !== MolecularViewerStyle.Tube,
);

export enum ViewAngle {
  PositiveXDirection = 'Positive X',
  NegativeXDirection = 'Negative X',
  PositiveYDirection = 'Positive Y',
  NegativeYDirection = 'Negative Y',
  PositiveZDirection = 'Positive Z',
  NegativeZDirection = 'Negative Z',
}

export const CHAMBER_VIEW_ANGLES = [
  { value: ViewAngle.PositiveXDirection, label: 'molecularViewer.PositiveXDirection' },
  { value: ViewAngle.NegativeXDirection, label: 'molecularViewer.NegativeXDirection' },
  { value: ViewAngle.PositiveYDirection, label: 'molecularViewer.PositiveYDirection' },
  { value: ViewAngle.NegativeYDirection, label: 'molecularViewer.NegativeYDirection' },
  { value: ViewAngle.PositiveZDirection, label: 'molecularViewer.PositiveZDirection' },
  { value: ViewAngle.NegativeZDirection, label: 'molecularViewer.NegativeZDirection' },
];

// Restricted to surface modes — pairing two opaque structural modes (e.g.
// ball-and-stick + stick) z-fights badly, but a translucent surface (or a
// dot cloud) laid over any structural mode is the canonical "structure +
// envelope" use case.
export const OVERLAY_STYLE_LABELS = [
  { value: MolecularViewerStyle.QuickSurface, label: 'molecularViewer.QuickSurface' },
  { value: MolecularViewerStyle.ContactSurface, label: 'molecularViewer.ContactSurface' },
  { value: MolecularViewerStyle.SolventAccessibleSurface, label: 'molecularViewer.SolventAccessibleSurface' },
  { value: MolecularViewerStyle.SolventExcludedSurface, label: 'molecularViewer.SolventExcludedSurface' },
  {
    value: MolecularViewerStyle.ElectrostaticPotentialSurface,
    label: 'molecularViewer.ElectrostaticPotentialSurface',
  },
  { value: MolecularViewerStyle.DotSurface, label: 'molecularViewer.DotSurface' },
];

export const GALLERY_STYLE_LABELS = [
  { value: MolecularViewerStyle.Sketch2D, label: 'molecularViewer.Sketch2D' },
  { value: MolecularViewerStyle.Sketch2DCharge, label: 'molecularViewer.Sketch2DCharge' },
  { value: MolecularViewerStyle.BallAndStick, label: 'molecularViewer.BallAndStick' },
  { value: MolecularViewerStyle.Wireframe, label: 'molecularViewer.Wireframe' },
  { value: MolecularViewerStyle.Stick, label: 'molecularViewer.Stick' },
  { value: MolecularViewerStyle.SpaceFilling, label: 'molecularViewer.SpaceFilling' },
  { value: MolecularViewerStyle.QuickSurface, label: 'molecularViewer.QuickSurface' },
  { value: MolecularViewerStyle.ContactSurface, label: 'molecularViewer.ContactSurface' },
  { value: MolecularViewerStyle.SolventAccessibleSurface, label: 'molecularViewer.SolventAccessibleSurface' },
  { value: MolecularViewerStyle.SolventExcludedSurface, label: 'molecularViewer.SolventExcludedSurface' },
  {
    value: MolecularViewerStyle.ElectrostaticPotentialSurface,
    label: 'molecularViewer.ElectrostaticPotentialSurface',
  },
  { value: MolecularViewerStyle.DotSurface, label: 'molecularViewer.DotSurface' },
];
